import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Download, RotateCcw } from 'lucide-react';
import { useImageFilters, DEFAULT_FILTERS, type FilterSettings } from '@/hooks/useImageFilters';
import type { UploadedImage } from '@/pages/ImageToolsHub';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import { toast } from 'sonner';

interface FiltersToolProps {
  images: UploadedImage[];
}

const PRESETS = [
  { value: 'none', label: 'No Filter', preview: '🔲' },
  { value: 'bw', label: 'Black & White', preview: '⬛' },
  { value: 'sepia', label: 'Sepia', preview: '🟫' },
  { value: 'vintage', label: 'Vintage', preview: '📷' },
  { value: 'cinematic', label: 'Cinematic', preview: '🎬' },
  { value: 'vibrant', label: 'Vibrant', preview: '🌈' },
  { value: 'faded', label: 'Faded', preview: '🌫️' },
  { value: 'high-contrast', label: 'High Contrast', preview: '⚡' },
];

export const FiltersTool: React.FC<FiltersToolProps> = ({ images }) => {
  const { isProcessing, progress, filteredImages, processImages, downloadAll } = useImageFilters();
  
  const [settings, setSettings] = useState<FilterSettings>(DEFAULT_FILTERS);

  const selectedImages = images.filter(img => img.selected);

  const handleProcess = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    await processImages(selectedImages, settings);
    toast.success(`Applied filters to ${selectedImages.length} image(s)`);
  };

  const handleDownloadAll = () => {
    downloadAll(filteredImages, 'filtered');
    toast.success('Downloaded all filtered images');
  };

  const handleReset = () => {
    setSettings(DEFAULT_FILTERS);
    toast.success('Reset all filters');
  };

  const updateSetting = (key: keyof FilterSettings, value: number | string) => {
    setSettings({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Preset Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Preset Filters</CardTitle>
          <CardDescription>Apply popular Instagram-style filters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((preset) => (
              <Button
                key={preset.value}
                variant={settings.preset === preset.value ? 'default' : 'outline'}
                onClick={() => updateSetting('preset', preset.value)}
                className="flex flex-col h-auto p-3"
              >
                <span className="text-2xl mb-1">{preset.preview}</span>
                <span className="text-xs">{preset.label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Basic Adjustments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Basic Adjustments</CardTitle>
              <CardDescription>Fine-tune brightness, contrast, and color</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset All
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Brightness */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Brightness</Label>
              <span className="text-sm text-muted-foreground">{settings.brightness}</span>
            </div>
            <Slider
              value={[settings.brightness]}
              onValueChange={([value]) => updateSetting('brightness', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Contrast */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Contrast</Label>
              <span className="text-sm text-muted-foreground">{settings.contrast}</span>
            </div>
            <Slider
              value={[settings.contrast]}
              onValueChange={([value]) => updateSetting('contrast', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Saturation */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Saturation</Label>
              <span className="text-sm text-muted-foreground">{settings.saturation}</span>
            </div>
            <Slider
              value={[settings.saturation]}
              onValueChange={([value]) => updateSetting('saturation', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Exposure */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Exposure</Label>
              <span className="text-sm text-muted-foreground">{settings.exposure}</span>
            </div>
            <Slider
              value={[settings.exposure]}
              onValueChange={([value]) => updateSetting('exposure', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Highlights */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Highlights</Label>
              <span className="text-sm text-muted-foreground">{settings.highlights}</span>
            </div>
            <Slider
              value={[settings.highlights]}
              onValueChange={([value]) => updateSetting('highlights', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Shadows */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Shadows</Label>
              <span className="text-sm text-muted-foreground">{settings.shadows}</span>
            </div>
            <Slider
              value={[settings.shadows]}
              onValueChange={([value]) => updateSetting('shadows', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>
        </CardContent>
      </Card>

      {/* Color Adjustments */}
      <Card>
        <CardHeader>
          <CardTitle>Color Correction</CardTitle>
          <CardDescription>Adjust temperature, tint, and hue</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Temperature */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Temperature</Label>
              <span className="text-sm text-muted-foreground">
                {settings.temperature < 0 ? 'Cool' : settings.temperature > 0 ? 'Warm' : 'Neutral'}
              </span>
            </div>
            <Slider
              value={[settings.temperature]}
              onValueChange={([value]) => updateSetting('temperature', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Tint */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Tint</Label>
              <span className="text-sm text-muted-foreground">
                {settings.tint < 0 ? 'Magenta' : settings.tint > 0 ? 'Green' : 'Neutral'}
              </span>
            </div>
            <Slider
              value={[settings.tint]}
              onValueChange={([value]) => updateSetting('tint', value)}
              min={-100}
              max={100}
              step={1}
            />
          </div>

          {/* Hue */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Hue Shift</Label>
              <span className="text-sm text-muted-foreground">{settings.hue}°</span>
            </div>
            <Slider
              value={[settings.hue]}
              onValueChange={([value]) => updateSetting('hue', value)}
              min={0}
              max={360}
              step={1}
            />
          </div>
        </CardContent>
      </Card>

      {/* Advanced Effects */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Effects</CardTitle>
          <CardDescription>Add vignette, grain, and blur</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Blur */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Blur</Label>
              <span className="text-sm text-muted-foreground">{settings.blur}px</span>
            </div>
            <Slider
              value={[settings.blur]}
              onValueChange={([value]) => updateSetting('blur', value)}
              min={0}
              max={20}
              step={1}
            />
          </div>

          {/* Vignette */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Vignette</Label>
              <span className="text-sm text-muted-foreground">{settings.vignette}%</span>
            </div>
            <Slider
              value={[settings.vignette]}
              onValueChange={([value]) => updateSetting('vignette', value)}
              min={0}
              max={100}
              step={1}
            />
          </div>

          {/* Grain */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Film Grain</Label>
              <span className="text-sm text-muted-foreground">{settings.grain}%</span>
            </div>
            <Slider
              value={[settings.grain]}
              onValueChange={([value]) => updateSetting('grain', value)}
              min={0}
              max={100}
              step={1}
            />
          </div>
        </CardContent>
      </Card>

      {/* Process Button */}
      <Card>
        <CardContent className="pt-6">
          <Button
            onClick={handleProcess}
            disabled={isProcessing || selectedImages.length === 0}
            className="w-full"
          >
            {isProcessing ? 'Applying Filters...' : `Apply to ${selectedImages.length} Image(s)`}
          </Button>

          {isProcessing && (
            <div className="space-y-2 mt-4">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">{Math.round(progress)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {filteredImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Filtered Images</CardTitle>
                <CardDescription>{filteredImages.length} images processed</CardDescription>
              </div>
              <Button onClick={handleDownloadAll}>
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {filteredImages.map((image) => (
              <BeforeAfterComparison
                key={image.id}
                beforeImage={image.original}
                afterImage={image.filtered}
                beforeLabel="Original"
                afterLabel="Filtered"
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
